import { Button, Dialog, Input, MenuItem, Select, VSpacer } from '@/components/DesignSystem';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { IntegrationApi } from '@/utilities/api/IntegrationApi';
import { ApiSecureIntegration } from '@api/interfaces/ApiIntegration';
import { Stack } from '@mui/material';
import { IntegrationType } from '@shared/enums';
import React, { useState } from 'react';

interface SaveIntegrationDetailsModalProps {
  existingIntegration: ApiSecureIntegration | null,
  onClose: () => void,
  onSave: () => void,
  retailerId: string,
}

export const SaveIntegrationDetailsModal = ({
  existingIntegration,
  onClose,
  onSave,
  retailerId,
}: SaveIntegrationDetailsModalProps) => {
  const integrationTypes = Object.values(IntegrationType);
  const [selectedIntegrationType, setSelectedIntegrationType] = (
    useState(existingIntegration?.integrationType)
  );
  const [apiKey, setApiKey] = useState(existingIntegration?.apiKey);
  const [databaseId, setDatabaseId] = useState(existingIntegration?.databaseId);
  const [user, setUser] = useState(existingIntegration?.user);
  const [apiSecret, setApiSecret] = useState(existingIntegration?.apiSecret);
  const [email, setEmail] = useState(existingIntegration?.email);
  const [isSaving, setIsSaving] = useState(false);
  const { openSnackbar } = useSnackbar();
  const isFormValid = !!(
    selectedIntegrationType && apiKey && databaseId && user && apiSecret
  );
  const isReadOnly = !!existingIntegration;

  const handleSave = async () => {
    if (!isFormValid) {
      return;
    }
    setIsSaving(true);
    try {
      await IntegrationApi.createForRetailer(retailerId, {
        apiKey,
        apiSecret,
        databaseId,
        email,
        integrationType: selectedIntegrationType,
        user,
      });
      openSnackbar('Integration successfully saved');
      onSave();
      onClose();
    } catch (e: any) {
      openSnackbar(`Error saving integration: ${e.message}`);
    }
    setIsSaving(false);
  };

  return (
    <Dialog
      acceptButton={(props) => existingIntegration ? null : (
        <Button
          {...props}
          disabled={!isFormValid}
          loading={isSaving}
          onClick={handleSave}
          variant="contained"
        >
          Save
        </Button>
      )}
      cancelButton={(props) => (
        <Button {...props} onClick={onClose}>
          {existingIntegration ? 'Close' : 'Cancel'}
        </Button>
      )}
      onClose={onClose}
      open
      showCloseButton={false}
      testID="save-integration-details-modal"
      title="Integration details"
    >
      <Select
        label="Integration type"
        onChangeValue={(value) => setSelectedIntegrationType(value as IntegrationType)}
        readOnly={isReadOnly}
        testID="integration-type-select"
        value={selectedIntegrationType}
      >
        {integrationTypes.map((integrationType) => (
          <MenuItem
            key={integrationType}
            testID="integration-type-menu-item"
            value={integrationType}
          >
            {integrationType}
          </MenuItem>
        ))}
      </Select>
      <VSpacer size="5" />
      {selectedIntegrationType === IntegrationType.Agvance && (
        <Stack>
          <VSpacer size="5" />
          <Input
            InputProps={{ readOnly: isReadOnly }}
            label="Database ID"
            onChangeText={setDatabaseId}
            testID="db-id-input"
            value={databaseId}
          />
          <VSpacer size="5" />
          <Input
            InputProps={{ readOnly: isReadOnly }}
            label="API key"
            onChangeText={setApiKey}
            testID="api-key-input"
            value={apiKey}
          />
          <VSpacer size="5" />
          <Input
            InputProps={{ readOnly: isReadOnly }}
            label="User ID"
            onChangeText={setUser}
            testID="user-input"
            value={user}
          />
          <VSpacer size="5" />
          <Input
            InputProps={{ readOnly: isReadOnly }}
            label="Password"
            onChangeText={setApiSecret}
            testID="password-input"
            value={apiSecret}
          />
          <VSpacer size="5" />
          <Input
            InputProps={{ readOnly: isReadOnly }}
            label="Email"
            onChangeText={setEmail}
            testID="email-input"
            value={email}
          />
        </Stack>
      )}
    </Dialog>
  );
};
